import { useCallback, useEffect } from 'react'
import { BestOffer, CheapestDateRequest, CheapestDates } from '../api/calendar'
import { nextVersion, useMonotonicState } from '@/v2/common/monotonic-state'
import api from '@/services/api'

const areEquals = (req1: CheapestDateRequest, req2: CheapestDateRequest) =>
  req1.origin === req2.origin && req1.destination === req2.destination

type LoadingState = {
  loading: boolean
  req?: CheapestDateRequest
  bestOffers?: CheapestDates
}

export function useBestOffer(): [
  boolean,
  CheapestDates | undefined,
  (req: CheapestDateRequest) => void
] {
  // FIXME a reduce would have been a better option
  const [result, setResult] = useMonotonicState<LoadingState>({
    loading: false,
  })

  const updateRequest = useCallback(
    (request: CheapestDateRequest) =>
      setResult((old: LoadingState) => {
        if (!request) {
          return old
        }

        if (!old.req) {
          return {
            loading: true,
            req: request,
          }
        }

        return areEquals(old.req, request)
          ? old
          : {
              loading: true,
              req: request,
              bestOffers: old.bestOffers,
            }
      }),
    [setResult]
  )

  // check if the request change
  useEffect(() => {
    if (result.req) {
      const r = result.req
      const v = nextVersion()
      api.calendar
        .getCheapestDates(r)
        .then((d) => {
          setResult(
            {
              loading: false,
              req: r,
              bestOffers: d.data ?? [],
            },
            v
          )
        })
        .catch(() => {
          // an error occured, stop loading, keep the previous offers, and authorize to reload
          setResult((old) => ({
            loading: false,
            bestOffers: old?.bestOffers,
          }))
        })
    }
  }, [result.req])

  return [result.loading, result.bestOffers, updateRequest]
}

export function composeUniqueOfferId(offer: BestOffer) {
  return [
    offer.origin.id,
    offer.destination.id,
    offer.departure_date,
    offer.return_date,
  ].join('-')
}
